import {AuthModel} from './_models'
import {AdminAuth, clearAdminAuth} from './AdminAuth'
import {globalLoadingBegin, globalLoadingEnd} from '../../../loading/globalLoadingBus'

/** Pathname from absolute or relative request URL (for admin API matching). */
function requestPathname(configUrl: string | undefined): string {
  if (!configUrl) return ''
  try {
    return new URL(configUrl, 'http://localhost').pathname
  } catch {
    return configUrl.split('?')[0]
  }
}

/**
 * 401 here means wrong current password / verification — session is still valid; do not wipe storage.
 */
function isAdminAuthUserError401Path(pathname: string): boolean {
  const p = pathname.toLowerCase()
  const only = ['/admin/auth/change-password', '/admin/auth/2fa/disable']
  return only.some((seg) => p === seg || p.endsWith(seg))
}

function shouldInvalidateAdminSessionOn401(err: any): boolean {
  const status = err?.response?.status
  if (status !== 401) return false
  const path = requestPathname(err?.config?.url)
  if (!path.toLowerCase().includes('/admin')) return false
  if (isAdminAuthUserError401Path(path)) return false
  const hadTokenHeader = !!err?.config?.headers?.['x-access-token']
  const hadStored = !!AdminAuth()
  return hadTokenHeader || hadStored
}

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: any) => {
      const auth = getAuth()
      if (auth && auth.api_token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${auth.api_token}`
      }

      // Full-screen Shrido loader for admin API traffic (opt out with skipLoader: true).
      if (!config?.skipLoader && !config?.headers?.['X-Skip-Loader']) {
        globalLoadingBegin()
        config.__shridoLoaderTracked = true
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  const endLoader = (config: any) => {
    if (config?.__shridoLoaderTracked) {
      globalLoadingEnd()
    }
  }

  axios.interceptors.response.use(
    (r: any) => {
      endLoader(r?.config)
      return r
    },
    (err: any) => {
      endLoader(err?.config)
      if (shouldInvalidateAdminSessionOn401(err)) {
        clearAdminAuth()
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
          window.location.assign('/auth')
        }
      }
      return Promise.reject(err)
    }
  )
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
